import { FormAggregatedResultElement } from "@/components/form-result/aggregated-result/components/FormAggregatedResultElement.tsx";
import { FormAggregatedResultFilterOptions } from "@/components/form-result/aggregated-result/components/types.ts";
import { GoalCard } from "@/pages/talent/goals/components/GoalCard.tsx";
import { Text, useModals, Box, Button, Stack, Title } from "@skailer-com/uikit";
import { PerformanceReviewQuestionResultsModal } from "@/pages/talent/performance-reviews/view/widgets/PerformanceReviewQuestionResultsModal.tsx";
import { ApiAIProcessingStatus } from "@/types/ai/ai.types.ts";
import { ApiFormViewElement } from "@/types/forms/form-view.types.ts";
import { ApiFormElementType } from "@/types/forms/form.types.ts";
import {
  ApiFormMergedElement,
  ApiPerformanceReviewParticipantElementsReport,
} from "@/types/performance-review.types.ts";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const PerformanceReviewParticipantElements = ({
  performanceReviewId,
  participantId,
  reviewTypeId,
  elementsReport,
}: {
  performanceReviewId: number;
  reviewTypeId?: number;
  participantId: number;
  elementsReport: ApiPerformanceReviewParticipantElementsReport;
}) => {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const [shownBreakdowns, setShownBreakdowns] = useState<Set<string>>(new Set());

  const toggleBreakdown = (key: string) => {
    setShownBreakdowns((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) newSet.delete(key);
      else newSet.add(key);
      return newSet;
    });
  };

  const getElementTitle = (element: ApiFormViewElement) => {
    let title: string | null = null;

    switch (element.type) {
      case ApiFormElementType.text:
        title = element.text_settings?.title || null;
        break;
      case ApiFormElementType.open_answer:
        title = element.open_answer_settings?.title || null;
        break;
      case ApiFormElementType.scale:
        title = element.scale_settings?.title || null;
        break;
      case ApiFormElementType.select:
        title = element.select_settings?.title || null;
        break;
      case ApiFormElementType.employee_nps:
        title = t("surveys:employee_nps_element_title_default");
        break;
      case ApiFormElementType.goals:
        title = element.goals_settings?.title ?? t("goals:goal");
        break;
    }

    return title;
  };

  const handleShowResultElementDetailModal = (
    element: ApiFormMergedElement,
    filterOptions?: FormAggregatedResultFilterOptions
  ) => {
    openModal(PerformanceReviewQuestionResultsModal, {
      performanceReviewId: performanceReviewId,
      reviewTypeId: reviewTypeId,
      participantId: participantId,
      element: element.element,
      elementIds: element.element_ids,
      referenceId: element.element.reference_id,
      answerScaleOptionIds: filterOptions?.answer_scale_option_ids,
      answerSelectOptionIds: filterOptions?.answer_select_option_ids,
      selectableOptionIds: filterOptions?.selectable_option_ids,
      resultAggregation: element.result_aggregation,
      managers: filterOptions?.managers,
      departments: filterOptions?.departments,
    });
  };

  const oneSection = elementsReport.sections.length === 1;
  return (
    <Stack gap={"2xl"}>
      {elementsReport.sections.map((section, sectionIndex) => {
        if (section.elements.length == 0) return;
        return (
          <Box key={`section-${sectionIndex}-${section.title}`}>
            {!oneSection && (
              <Stack direction={"horizontal"} gap={"md"} className={"pb-6"}>
                <Stack items={"end"} className={"md:-ml-12 md:w-8"} justify={"end"}>
                  <Title className={"text-secondary"} size={"lg"} header={`${sectionIndex + 1}`} />
                </Stack>
                <Stack className={"flex-1"} justify={"end"}>
                  <Title
                    header={section.title || t("common:untitled")}
                    size={"lg"}
                    className={section.title ? "" : "text-secondary"}
                  />
                </Stack>
              </Stack>
            )}
            <Stack gap={"2xl"}>
              {section.elements.map((element, elementIndex) => {
                const title = getElementTitle(element.element) ?? "";
                const avgScore = element.scale_score.avg_scale_score;
                const maxScore = element.scale_score.max_scale_score;
                const aggregationData =
                  element.element.type === ApiFormElementType.goals
                    ? element.result_aggregation?.goals_data
                    : element.result_aggregation?.scale_data;
                const responsesCount =
                  aggregationData?.options.reduce((sum, opt) => sum + opt.count, 0) ?? 0;
                const breakdownKey = `${sectionIndex}-${element.element.id}-${element.element.reference_id}`;
                const isShowBreakdown = shownBreakdowns.has(breakdownKey);
                const useBreakdown = Boolean(avgScore);

                return (
                  <Box key={`element-${elementIndex}-${element.element.id}`}>
                    <Stack direction={"horizontal"} gap={"md"} className={"pb-2"}>
                      <Stack items={"end"} className={"md:-ml-12 md:w-8"} justify={"start"}>
                        <Title
                          size={"xs"}
                          header={`${oneSection ? "" : sectionIndex + 1 + "."}${elementIndex + 1}`}
                        />
                      </Stack>

                      <Stack className={"flex-1 w-full"} justify={"end"}>
                        {useBreakdown ? (
                          <Stack gap={"smd"} className={"pb-4"}>
                            <Title size={"sm"} header={title} />
                            {element.element.goals?.map((goal) => (
                              <GoalCard
                                key={goal.id}
                                goal={goal}
                                to={`/goals/${goal.id}`}
                                target={"_blank"}
                                clickable
                                disableDecoration={true}
                              />
                            ))}
                          </Stack>
                        ) : (
                          <FormAggregatedResultElement
                            element={element.element}
                            results={{
                              elements: element.result_aggregation
                                ? [element.result_aggregation]
                                : [],
                              is_below_anonymity_threshold: false,
                              ai_summary: null,
                              ai_processing_status: ApiAIProcessingStatus.disabled,
                            }}
                            showDetailModalCallback={(_el, filterOptions) =>
                              handleShowResultElementDetailModal(element, filterOptions)
                            }
                          />
                        )}
                      </Stack>
                    </Stack>
                    <Box>
                      <Stack gap={"sm"}>
                        {useBreakdown && (
                          <>
                            <Stack direction={"horizontal"} gap={"sm"} items={"center"}>
                              {avgScore && (
                                <Stack>
                                  <Text className={"text-2xl"}>
                                    <Text className={"font-semibold"}>{avgScore.toFixed(1)}</Text>
                                    {t("common:of", { current: "", total: maxScore?.toFixed(1) })}
                                  </Text>
                                  <Text className={"text-secondary text-lg"}>
                                    {t("plurals:average_score_based_on_responses", {
                                      count: responsesCount,
                                    })}
                                  </Text>
                                </Stack>
                              )}
                            </Stack>
                            {element.result_aggregation && (
                              <Stack direction={"horizontal"} className={"pt-2"}>
                                <Button
                                  onClick={() => {
                                    toggleBreakdown(breakdownKey);
                                  }}
                                  variant={"secondary"}
                                >
                                  {isShowBreakdown
                                    ? t("common:hide_breakdown")
                                    : t("common:show_breakdown")}
                                </Button>
                              </Stack>
                            )}
                          </>
                        )}

                        {isShowBreakdown && element.result_aggregation && (
                          <Box className={"pt-4"}>
                            <FormAggregatedResultElement
                              element={element.element}
                              results={{
                                elements: [element.result_aggregation],
                                is_below_anonymity_threshold: false,
                                ai_summary: null,
                                ai_processing_status: ApiAIProcessingStatus.disabled,
                              }}
                              showDetailModalCallback={(_el, filterOptions) =>
                                handleShowResultElementDetailModal(element, filterOptions)
                              }
                            />
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};
